import React from "react";
import { X, ExternalLink, MapPin, Phone, Clock } from "lucide-react";

/**
 * In-app preview that renders the REAL public layout (hero + gallery + details) from the
 * current editor form data, so an operator sees exactly how the page will look before
 * publishing. `publicUrl` (when the item is live) opens the actual public page.
 */
export const PublicPreviewModal = ({ data, kind = "generic", publicUrl, onClose }) => {
  const cover = data.cover || data.images?.[0] || data.gallery?.[0] || "";
  const gallery = (data.images || data.gallery || []).slice(0, 8);
  const title = data.title || data.brand_name || data.name || "Anteprima";
  const desc = data.description || data.short_description || "";
  const priceLabel = data.price ? `da €${data.price}${data.pricing_type === "per_person" ? " / persona" : ""}` : null;
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-start justify-center overflow-y-auto p-4" data-testid="public-preview-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl my-8 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-secondary/10 text-secondary text-[11px] font-bold uppercase tracking-wide px-2 py-0.5">Anteprima pubblica</span>
          </div>
          <div className="flex items-center gap-3">
            {publicUrl && <a href={publicUrl} target="_blank" rel="noopener noreferrer" data-testid="preview-open-public" className="text-sm font-semibold text-primary inline-flex items-center gap-1">Apri pagina pubblica <ExternalLink className="h-3.5 w-3.5" /></a>}
            <button onClick={onClose} data-testid="preview-close"><X className="h-5 w-5 text-slate-400" /></button>
          </div>
        </div>
        <div className="max-h-[75vh] overflow-y-auto">
          {cover ? <img src={cover} alt="" className="w-full h-56 object-cover" /> : <div className="w-full h-56 bg-slate-100 flex items-center justify-center text-slate-300 text-sm">Nessuna copertina</div>}
          <div className="p-6">
            <h1 className="font-heading font-bold text-2xl text-slate-900">{title}</h1>
            {data.category && <p className="text-sm text-secondary font-semibold mt-1">{data.subcategory || data.category}</p>}
            {priceLabel && <p className="text-lg font-bold text-primary mt-2">{priceLabel}</p>}
            {desc && <p className="text-slate-600 mt-4 whitespace-pre-line">{desc}</p>}
            <div className="flex flex-wrap gap-4 mt-4 text-sm text-slate-500">
              {(data.address || data.city || data.meeting_point) && <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" /> {data.meeting_point || [data.address, data.city].filter(Boolean).join(", ")}</span>}
              {data.phone && <span className="inline-flex items-center gap-1"><Phone className="h-4 w-4" /> {data.phone}</span>}
              {data.hours && <span className="inline-flex items-center gap-1"><Clock className="h-4 w-4" /> {data.hours}</span>}
            </div>
            {gallery.length > 1 && (
              <div className="grid grid-cols-4 gap-2 mt-5">
                {gallery.map((g, i) => <img key={`${g}-${i}`} src={g} alt="" className="h-20 w-full object-cover rounded-lg" />)}
              </div>
            )}
            {Array.isArray(data.included) && data.included.length > 0 && (
              <div className="mt-5">
                <h3 className="font-heading font-semibold text-slate-800 mb-1">Incluso</h3>
                <ul className="list-disc pl-5 text-sm text-slate-600">{data.included.map((x, i) => <li key={i}>{x}</li>)}</ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/** Guided completion checklist shown inside editors. */
export const CompletionChecklist = ({ percent = 0, checklist = {}, labels = {}, testid = "completion" }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <span className="font-heading font-semibold text-slate-900 text-sm">Completamento profilo</span>
      <span className="font-bold text-primary" data-testid={`${testid}-percent`}>{percent}%</span>
    </div>
    <div className="h-2 rounded-full bg-slate-100 overflow-hidden mb-3">
      <div className="h-full bg-primary transition-all" style={{ width: `${percent}%` }} />
    </div>
    <ul className="grid sm:grid-cols-2 gap-1.5">
      {Object.entries(checklist).map(([k, ok]) => (
        <li key={k} className={`flex items-center gap-2 text-xs ${ok ? "text-emerald-600" : "text-slate-400"}`}>
          <span className={`h-4 w-4 rounded-full flex items-center justify-center text-[10px] ${ok ? "bg-emerald-100" : "bg-slate-100"}`}>{ok ? "✓" : "•"}</span>
          {labels[k] || k}
        </li>
      ))}
    </ul>
  </div>
);
